import apiClient from "./client";

// Gifting Flavor A — read-only shapes for the received-gift inbox.
// Mirrors the backend ReceivedGiftSerializer. The counterparty's phone/CPF is
// intentionally NOT exposed; never expect it here.
export interface GiftTicket {
  game_name: string;
  draw_number: string | number;
  draw_time: string;
  numbers: (string | number)[];
  quota: number;
}

export interface ReceivedGift {
  id: number;
  sender_name: string;
  message: string;
  status: number;
  status_label: string;
  claim_deadline: string | null;
  created_at: string;
  claimed_at: string | null;
  tickets: GiftTicket[];
}

// Sent gifts — same shape as ReceivedGift but with recipient_name, and every
// status, not just PENDING.
export interface SentGift {
  id: number;
  recipient_name: string;
  message: string;
  status: number;
  status_label: string;
  claim_deadline: string | null;
  created_at: string;
  claimed_at: string | null;
  tickets: GiftTicket[];
  /**
   * The bearer claim credential for a share-by-link (Flavor C) gift, returned
   * ONLY to the sender and ONLY while the link is live (share_by_link && PENDING).
   * null for an addressed Flavor A gift and for any spent/dead link. NEVER build
   * a /presente/ URL without checking this is a non-empty string.
   */
  claim_token?: string | null;
  /**
   * The sender's PRIVATE organizer note ("Ana"). Sender-only — the backend exposes
   * it on this serializer alone, never on the recipient inbox or the anonymous
   * preview. Empty for a gift sent before the composer, or with no name set.
   */
  label?: string;
  /**
   * True for a share-by-link (Flavor C) gift. Durable link identity: unlike
   * claim_token (which goes null on claim), this survives every status, so the
   * outbox can tell a claimed LINK gift from a claimed ADDRESSED one — and reconcile
   * the "link never minted" warning against links of ANY status.
   */
  share_by_link?: boolean;
  /**
   * The public 32-char order id (Order.order_id) this gift was minted in, or null.
   * Matches the id returned by the checkout response — reconcile against THIS, not
   * any numeric pk. Present on gifts minted after the order-attribution change.
   */
  order_id?: string | null;
  /**
   * The GROSS price the sender paid for the gifted cota(s) — Σ Ticket.bet across the
   * gift's tickets, i.e. the face value BEFORE any order-level gift discount, for the
   * whole gift rather than per-cota. A dot-decimal string with FOUR decimal places
   * ("8.5000"), because it serializes a Decimal; pass it through StrToCurrency rather
   * than interpolating it, or it renders as "R$ 8.5000".
   *
   * SENDER-OUTBOX ONLY — never on the recipient inbox or the anonymous preview.
   * Optional: absent on gifts minted before the serializer exposed it, and "0" for an
   * empty-ticket gift. Render nothing rather than "R$ 0,00" in either case.
   */
  amount?: string;
}

/**
 * The anonymous, pre-claim preview of a share-by-link gift.
 * GET /api/gift/claim/<token>/ — deliberately a DENY-list: no token, no PK, no
 * ids, no sender PII, no prize. `tickets` is [] for any non-PENDING gift.
 */
export interface GiftPreview {
  sender_name: string;
  message: string;
  status: number;
  status_label: string;
  claim_deadline: string | null;
  tickets: GiftTicket[];
}

/**
 * Gift.Status — the backend IntegerChoices enum (gifting/models.py).
 * Switch on THIS, never on the BR-PT status_label: labels are display copy and
 * may be retranslated at any time (CONVENTIONS #13).
 */
export enum GiftStatus {
  PENDING = 1,
  CLAIMED = 2,
  EXPIRED = 3,
  RETRACTED = 4,
  REFUNDED = 5,
  REJECTED = 6,
}

export type GiftStatusKind =
  | "pendente"
  | "aceito"
  | "expirado"
  | "cancelado"
  | "reembolsado"
  | "recusado";

/** Classify a gift by its integer status. Unknown values degrade to "pendente". */
export function classifyGiftStatus(gift: { status?: number }): GiftStatusKind {
  switch (gift.status) {
    case GiftStatus.CLAIMED:
      return "aceito";
    case GiftStatus.EXPIRED:
      return "expirado";
    case GiftStatus.RETRACTED:
      return "cancelado";
    case GiftStatus.REFUNDED:
      return "reembolsado";
    case GiftStatus.REJECTED:
      return "recusado";
    case GiftStatus.PENDING:
    default:
      return "pendente";
  }
}

// Gift error codes live in the leaf module ./giftCodes (client.ts needs them too,
// and importing them from here would be a cycle). Re-exported so pages have one
// import site for the gift domain.
export * from "./giftCodes";

export const GiftService = {
  /** Inbox — gifts addressed to me that are PENDING acceptance (GET /api/gift/). */
  async getReceivedGifts() {
    const response: any = await apiClient.get("/api/gift/");
    return response;
  },
  /**
   * Accept a gift addressed to me. EMPTY body. The endpoint 404-hides
   * non-addressees, so only ever call this with an id taken from the inbox
   * list — never an arbitrary id.
   */
  async acceptGift(giftId: number) {
    const response: any = await apiClient.post(`/api/gift/${giftId}/accept/`);
    return response;
  },
  /** Gifts I have sent (all statuses) — GET /api/gift/sent/. */
  async getSentGifts() {
    const response: any = await apiClient.get("/api/gift/sent/");
    return response;
  },
  /**
   * Retract a gift I sent — POST /api/gift/${giftId}/retract/. EMPTY body. The
   * link stops working and the cota stays mine. Like accept, the endpoint 404-hides
   * non-senders, so only ever call this with an id taken from the sent list — never
   * an arbitrary id. Outcomes (the caller branches on response.code):
   *   0    → success; data is the updated (RETRACTED) SentGift.
   *   9001 → lost the race to a live claim ("já foi resgatado"); NOT a success.
   *   9011 → other terminal state ("não está mais disponível"); NOT a success.
   * On any non-zero code the global interceptor already toasts the backend's BR-PT
   * message, so the caller adds no error toast — it only refetches and suppresses
   * the success toast.
   */
  async retractGift(giftId: number) {
    const response: any = await apiClient.post(`/api/gift/${giftId}/retract/`);
    return response;
  },
  /**
   * Anonymous preview of a share-by-link gift — GET /api/gift/claim/<token>/.
   * PUBLIC (AllowAny): never send credentials-dependent assumptions here. The
   * token IS the credential, so it must never be logged or put in an error message.
   */
  async getGiftPreview(token: string) {
    const response: any = await apiClient.get(
      `/api/gift/claim/${encodeURIComponent(token)}/`,
    );
    return response;
  },
  /**
   * Claim a share-by-link gift — POST /api/gift/claim/<token>/.
   *
   * Three server-side branches, distinguished by the SESSION, not by the body:
   *  - member          → send NO body; the gift is claimed onto the current account.
   *  - existing phone  → returns 1001; the caller routes to login and re-POSTs.
   *  - new phone/guest → send the register-to-claim body; the server creates the
   *                      account, logs it in (session cookie) and claims.
   *
   * Attribution keys (ref_token / mlm_ref_code / params) are deliberately NOT sent:
   * the backend strips them on this path to guarantee a claimed gift never creates
   * a downline.
   */
  async claimGift(
    token: string,
    body?: {
      phone_number: string;
      password: string;
      birthday: string;
      verification_code: string;
    },
  ) {
    const response: any = await apiClient.post(
      `/api/gift/claim/${encodeURIComponent(token)}/`,
      body ?? {},
    );
    return response;
  },
  /**
   * Send the registration OTP for the claim's NEW-ACCOUNT branch —
   * POST /api/gift/claim/<token>/send-code/.
   *
   * The CGNAT-safe twin of /api/user/send-sms-verification-code/: a valid token in
   * the URL is REQUIRED before a single SMS is sent, and the throttle is keyed on the
   * token + phone, never the client IP — so a NAT'd cluster of real gift-claimers no
   * longer collides on the shared 5/min per-IP limit. The token is a bearer credential:
   * never log it, toast it, or echo it into an error message.
   *
   * Success is a bare 200 the renderer wraps to { code: 0 }. Because the URL carries
   * the token, this can refuse the whole claim with a gift-domain code (9000 disabled /
   * 9002 expired / 9008 not-found / 9011 not-available) or a 429 throttle — the claim
   * page renders its terminal screen for those. 1003 (resend freeze) / 1010 (bad phone)
   * stay field-level, exactly as the generic send-sms endpoint returns them.
   */
  async sendClaimCode(token: string, phoneNumber: string) {
    const response: any = await apiClient.post(
      `/api/gift/claim/${encodeURIComponent(token)}/send-code/`,
      { phone_number: phoneNumber },
    );
    return response;
  },
};
